import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  listInsuranceForReview,
  reviewInsurance,
  getInsuranceDownloadUrl,
} from "@/lib/insurance.functions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/admin/insurance")({
  head: () => ({ meta: [{ title: "Insurance Review — Admin" }] }),
  component: AdminInsurancePage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">{error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

function AdminInsurancePage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin", "insurance"],
    queryFn: () => listInsuranceForReview(),
  });

  const [notes, setNotes] = useState<Record<string, string>>({});
  const mut = useMutation({
    mutationFn: reviewInsurance,
    onSuccess: (r) => {
      if ("error" in r) toast.error(r.error);
      else { toast.success("Decision recorded."); qc.invalidateQueries({ queryKey: ["admin", "insurance"] }); }
    },
  });

  async function openDoc(path: string) {
    const res = await getInsuranceDownloadUrl({ data: { path } });
    if ("error" in res) { toast.error(res.error); return; }
    window.open(res.url, "_blank");
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin</p>
        <h1 className="text-3xl font-light">Insurance review</h1>

        <div className="mt-8 space-y-4">
          {(data?.items ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing awaiting review.</p>
          )}
          {(data?.items ?? []).map((row) => {
            const r = row as Record<string, string | null>;
            const id = r.id as string;
            return (
              <div key={id} className="border border-border p-5 text-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{r.full_name ?? "Bartender"}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.insurance_provider} · #{r.insurance_policy_number} · expires {r.insurance_expires_at}
                    </p>
                  </div>
                  <span className="text-[11px] uppercase tracking-wide-luxury text-muted-foreground">
                    {r.insurance_status}
                  </span>
                </div>
                {r.insurance_document_path && (
                  <Button variant="outline" size="sm" className="mt-3"
                    onClick={() => openDoc(r.insurance_document_path as string)}>
                    Open certificate
                  </Button>
                )}
                <Textarea className="mt-3" placeholder="Internal notes (optional)"
                  value={notes[id] ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [id]: e.target.value }))} />
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => mut.mutate({ data: { bartender_id: id, decision: "verified", notes: notes[id] ?? null } })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => mut.mutate({ data: { bartender_id: id, decision: "rejected", notes: notes[id] ?? null } })}>
                    Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
